import React, { useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * @typedef {Object} ConfirmOptions
 * @property {string} title - Dialog title
 * @property {string} [description] - Dialog description/body text
 * @property {string} [confirmText='Sim'] - Confirm button label
 * @property {string} [cancelText='Não'] - Cancel button label
 * @property {'destructive' | 'default'} [variant='destructive'] - Confirm button variant
 */

function ConfirmDialog({ title, description, confirmText = 'Sim', cancelText = 'Não', variant = 'destructive', onConfirm, onCancel }) {
  const [open, setOpen] = useState(true);

  const handleConfirm = useCallback(() => {
    setOpen(false);
    onConfirm();
  }, [onConfirm]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    onCancel();
  }, [onCancel]);

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          handleCancel();
        }
      }}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            {cancelText}
          </Button>
          <Button
            variant={variant}
            onClick={handleConfirm}
            autoFocus
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Imperative confirm dialog using Radix UI Dialog.
 * Drop-in replacement for SweetAlert2 confirm pattern.
 *
 * @param {ConfirmOptions} options
 * @returns {Promise<boolean>} resolves true if confirmed, false if cancelled
 *
 * @example
 * const confirmed = await confirm({
 *   title: 'Delete item?',
 *   description: 'This action cannot be undone.',
 *   confirmText: 'Yes',
 *   cancelText: 'No',
 *   variant: 'destructive'
 * });
 * if (confirmed) { // do something }
 */
export function confirm(options) {
  return new Promise(resolve => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = () => {
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 300);
    };

    root.render(
      <ConfirmDialog
        {...options}
        onConfirm={() => {
          resolve(true);
          cleanup();
        }}
        onCancel={() => {
          resolve(false);
          cleanup();
        }}
      />
    );
  });
}
